import dayjs from 'dayjs';
import {makeAutoObservable, reaction, runInAction, when} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {ConferenceApiModel} from '../../api/conference';
import type {FollowerApiModel} from '../../api/follower';
import type {
  CoreClientConferencesCreateParams,
  CoreClientConferencesListParams,
  CoreClientFollowersListParams,
} from '../../api/types/core/methods';
import type {ApiRpcQuery} from '../../api/utils/apiRequest';
import type {CalendarController} from './Calendar.controller';
import type {TimeslotsStore} from './Timeslots.store';
import type {Coach, ConferenceType, DataModel, Strategy} from './types';

import {currentUserStore} from '../../api/user';
import {apiRequest} from '../../api/utils/apiRequest';
import {not} from '../../utils/conditionalUtils';
import {datetimeObjToISOString} from '../../utils/datetime';
import {
  isConferenceEnded,
  isConferenceExpired,
} from '../../utils/entity/conference';
import {SCHEDULE_SLOT_DURATION} from '../../utils/entity/user';
import {
  FlagManager,
  InitialLoadManager,
  ReactionDisposeManager,
} from '../../utils/store';
import {isDefined} from '../../utils/typecheck';
import {BookConferenceStore} from './BookConference.store';
import {CoachesStore} from './Coaches.store';
import {ExternalIntroCallStrategy} from './IntroCallStrategy.external';
import {type TimeslotModel} from './Timeslot.model';
import {NoCoachError} from './utils/errors';

export class IntroCallStrategy implements Strategy {
  coachCoverage = null;
  coaches = new CoachesStore();
  conferenceToRescheduleId: null | string = null;
  conferenceType: ConferenceType = 'intro_call';
  dataTarget: DataModel | null = null;
  disposer = new ReactionDisposeManager();
  hasCoachCoverage = false;
  initialLoad = new InitialLoadManager();
  introCallConference: ConferenceApiModel | null = null;
  isIntroCallAvailable = new FlagManager();
  readonly isShowBuyMoreButton = false;
  readonly isShowCoachSelect = false;
  readonly shouldShowAvailableConferences = false;
  private calendar: CalendarController;
  private timeslots: TimeslotsStore;

  constructor(coach: Coach) {
    this.coaches.init([coach]);
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
    this.initialLoad.doneWhen(() => this.coaches.all.isNotEmpty);
  }

  async init(): Promise<void> {
    const coach = this.coaches.selected[0]!;

    this.calendar.init(coach.bookingRange);

    if (coach.schedule && coach.conferenceDuration) {
      await this.checkIsIntroCallAvailable();
    }

    if (
      currentUserStore.user &&
      currentUserStore.user.subtenant?.features.preregister
    ) {
      // external user can cancel a session and then
      // book a new intro call via coach practice UI!
      // For this reason, we need to set the min booking lead
      // minutes the same as in external strategy.
      this.timeslots.setMinBookingLeadMinutes(
        ExternalIntroCallStrategy.MIN_BOOKING_LEAD_MINUTES,
      );
    }
  }

  destruct() {
    this.disposer.dispose();
  }

  get conferenceBookingDateString() {
    if (!this.introCallConference) {
      return '';
    }

    const start = dayjs(
      datetimeObjToISOString(this.introCallConference.start_date),
    );
    const end = dayjs(
      datetimeObjToISOString(this.introCallConference.end_date),
    );

    return BookConferenceStore.bookDateToString(start, end);
  }

  get conferenceDuration() {
    const coach = this.coaches.selected[0]!;

    return coach.conferenceDuration || SCHEDULE_SLOT_DURATION;
  }

  get isTimeslotsLoaded() {
    return Boolean(this.timeslots?.initialLoad.isDone);
  }

  get shouldGuessTimezone() {
    return false;
  }

  get shouldShowBookButton() {
    return (
      this.initialLoad.isDone &&
      this.isIntroCallAvailable.isTrue &&
      Boolean(this.dataTarget?.shouldShowBookButton)
    );
  }

  get shouldShowTimezone() {
    return true;
  }

  get title() {
    return i18n.t('shared:schedule_session.intro_call');
  }

  async checkIsIntroCallAvailable() {
    const coach = this.coaches.selected[0]!;

    if (!currentUserStore.user) {
      return false;
    }

    const [followersResult, conferencesResult] = await apiRequest<
      CoreClientConferencesListParams | CoreClientFollowersListParams
    >({
      batch: [
        {
          method: 'client.followers.list',
          params: {
            query: [
              ['client_id', '==', currentUserStore.user._id],
              ['coach_id', '==', coach.id],
              ['status', '==', 'joined'] as ApiRpcQuery<
                FollowerApiModel,
                'status'
              >,
            ],
          } as CoreClientFollowersListParams,
        },
        {
          method: 'client.conferences.list',
          params: {
            query: [
              ['coach_ids', 'in', [coach.id]],
              ['created_by', '==', 'client'] as ApiRpcQuery<
                ConferenceApiModel,
                'created_by'
              >,
            ],
          } as CoreClientConferencesListParams,
        },
      ],
    });

    const targetConference = conferencesResult._items.filter(c =>
      not(isConferenceExpired(c)),
    )[0];

    const verdict = getVerdict(followersResult._items, targetConference);

    switch (verdict) {
      case 'introCallEnded': {
        this.isIntroCallAvailable.setFalse();
        this.introCallConference = targetConference;
        break;
      }
      case 'introCallHasBeenScheduled': {
        this.isIntroCallAvailable.setTrue();
        runInAction(() => {
          this.introCallConference = targetConference;
        });
        break;
      }
      case 'noScheduledIntroCall': {
        this.isIntroCallAvailable.setTrue();
        break;
      }
      case 'userIsCoachClientAlready':
        this.isIntroCallAvailable.setFalse();
        break;
      default: {
        return;
      }
    }

    function getVerdict(followers, conference?) {
      if (followers.length > 0) {
        return 'userIsCoachClientAlready';
      }

      if (isDefined(conference)) {
        if (isConferenceEnded(conference)) {
          return 'introCallEnded';
        }

        return 'introCallHasBeenScheduled';
      } else {
        return 'noScheduledIntroCall';
      }
    }
  }

  async deleteConferenceToReschedule(): Promise<void> {
    // do nothing
  }

  inject(calendar: CalendarController, timeslots: TimeslotsStore) {
    this.calendar = calendar;
    this.timeslots = timeslots;

    return this;
  }

  isCoverageCoachAvailableForSlot(): boolean {
    return true;
  }

  makeRequestParams(forSlot: TimeslotModel): CoreClientConferencesCreateParams {
    const coach = this.coaches.selected[0];

    if (coach) {
      const {end_date, start_date} = forSlot.makeRequestParams(
        coach.conferenceDuration || 0,
      );

      return {
        coach_ids: this.coaches.selectedIds.entries,
        end_date,
        start_date,
      };
    } else {
      throw new NoCoachError();
    }
  }

  async refresh(): Promise<void> {
    const coach = this.coaches.selected[0]!;

    if (coach.schedule && coach.conferenceDuration) {
      await this.checkIsIntroCallAvailable();
    }
  }

  refreshSlots({isSilent}: {isSilent?: boolean} = {}) {
    return this.timeslots.makeSlots({
      coaches: this.coaches.selected,
      duration: this.conferenceDuration,
      isSilent,
    });
  }

  setConferenceToRescheduleId(_id: null | string) {
    throw new Error(
      "It's not allowed to change conference to reschedule for IntroCallStrategy",
    );
  }

  setConferenceType(_type: ConferenceType) {
    throw new Error(
      "It's not allowed to change conference type for IntroCallStrategy",
    );
  }

  setupReactions(shouldFetchTimeslots: boolean) {
    this.disposer.watch(
      'set_data_target',
      when(
        () => isDefined(this.coaches.selected[0]),
        () => (this.dataTarget = this.coaches.selected[0]!),
      ),
    );

    const fetchTimeslots = () => {
      if (shouldFetchTimeslots) {
        this.refreshSlots();
      }
    };

    this.disposer.watch(
      'initial_load',
      when(
        () => this.initialLoad.isDone,
        () => {
          fetchTimeslots();

          this.disposer.watch(
            'change_range',
            reaction(
              () => this.calendar.range,
              () => {
                fetchTimeslots();
              },
            ),
          );
        },
      ),
    );

    return this;
  }
}
